import axios from 'axios';

// Avoid indefinite hang if backend is unreachable (e.g. wrong URL or server down)
axios.defaults.timeout = 30000; // 30 seconds

/**
 * Session token.
 *
 * The API now refuses anything without one. Kept beside the cached user in
 * localStorage so a refresh does not sign you out, attached to every request
 * here rather than at each call site — one place to get right.
 */
export const TOKEN_STORAGE_KEY = 'salesTrackerToken';

export const getAuthToken = () => {
  try { return localStorage.getItem(TOKEN_STORAGE_KEY) || null; } catch { return null; }
};

export const AUTH_EXPIRED_EVENT = 'ags-auth-expired';
// The server refuses everything but the password change until one is made.
export const PASSWORD_CHANGE_EVENT = 'ags-password-change-required';

export const setAuthToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      handlingAuthFailure = false; // a fresh sign-in re-arms the 401 handler
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch { /* private browsing */ }
};

axios.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// An expired or missing session means the whole app is dead in the water, so
// clear it and show the login screen instead of a hundred silent failures.
//
// This must NOT reload the page. Reloading re-mounts the app, which fires the
// same unauthenticated requests, which 401 again, which reloads again — the
// screen flickers and never settles. Signal React instead and let it render the
// login screen normally.
let handlingAuthFailure = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.code;
    if (status === 403 && code === 'PASSWORD_CHANGE_REQUIRED' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PASSWORD_CHANGE_EVENT));
      return Promise.reject(error);
    }
    if (status === 401 && !handlingAuthFailure) {
      handlingAuthFailure = true;
      try { localStorage.removeItem(TOKEN_STORAGE_KEY); } catch { /* ignore */ }
      try { localStorage.removeItem('salesTrackerUser'); } catch { /* ignore */ }
      const message = code === 'TOKEN_EXPIRED'
        ? 'Your session expired. Please sign in again.'
        : code === 'TOKEN_REVOKED'
          ? 'For security, everyone has been signed out. Please sign in again.'
          : 'Please sign in to continue.';
      if (typeof window !== 'undefined') {
        try { sessionStorage.setItem('authMessage', message); } catch { /* ignore */ }
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { message } }));
      }
    }
    return Promise.reject(error);
  }
);

// Production backend URL
const PRODUCTION_API_URL = 'https://backend-six-sooty-37.vercel.app/api';

const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.includes('localhost'))
      return PRODUCTION_API_URL;
  }
  return 'http://localhost:8000/api';
};

// Get API URL - this runs at module load time, but window check happens at runtime
let API_URL;
if (typeof window !== 'undefined') {
  API_URL = getApiUrl();
  console.log('🔗 API_URL configured as:', API_URL);
  console.log('🔗 REACT_APP_API_URL env var:', process.env.REACT_APP_API_URL || 'not set');
} else {
  // Server-side rendering fallback
  API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
}

export const getDashboardApiUrl = () => (typeof window !== 'undefined' ? getApiUrl() : API_URL);

// Simple response cache: returns stale data instantly while refreshing in the background.
// Eliminates the "Loading..." spinner when switching between tabs you've already visited.
const _apiCache = {};
// Two screens asking for the same thing at the same moment must not both go and
// get it. Opening the app fired the full lead list from App.js, from Highlights
// and from Residential Installations at once — three copies of a multi-megabyte
// response, three times the wait.
const _inFlight = {};

const cachedFetch = (cacheKey, fetchFn, ttlMs = 60000) => {
  const entry = _apiCache[cacheKey];
  const now = Date.now();
  if (!entry && _inFlight[cacheKey]) return _inFlight[cacheKey];
  if (entry && entry.data) {
    if (now - entry.ts < ttlMs) return Promise.resolve(entry.data);
    // Stale but present — return stale data immediately, refresh in background
    fetchFn().then(data => { _apiCache[cacheKey] = { data, ts: Date.now() }; }).catch(() => {});
    return Promise.resolve(entry.data);
  }
  // No cache — fetch and store, and let anyone who asks while it is in the air
  // wait on the same request.
  const request = fetchFn()
    .then(data => { _apiCache[cacheKey] = { data, ts: Date.now() }; return data; })
    .finally(() => { delete _inFlight[cacheKey]; });
  _inFlight[cacheKey] = request;
  return request;
};
// Call this after writes (create/update/delete) to bust cache for a prefix
const bustCache = (prefix) => {
  Object.keys(_apiCache).forEach(k => { if (k.startsWith(prefix)) delete _apiCache[k]; });
};

// Leads API
export const leadsAPI = {
  // Get all leads with optional filters.
  //
  // Shared, because this is the app's heaviest request — the whole table, which
  // the screens then filter client-side. App.js, Highlights and Residential
  // Installations each used to fetch their own copy on load. Sixty seconds of
  // cache, in-flight requests shared, and any write to a lead clears it.
  getAll: async (params = {}) => {
    const key = `leads|${JSON.stringify(params || {})}`;
    return cachedFetch(key, async () => {
      const response = await axios.get(`${API_URL}/leads`, { params });
      return response.data;
    });
  },

  // Every column, including the heavy text the list view leaves behind
  // (contact_book, company_overview, ai_chat_url). For the Excel backup, which
  // is meant to be a complete archive. Deliberately not cached.
  getAllFull: async (params = {}) => {
    const response = await axios.get(`${API_URL}/leads`, { params: { ...params, full: 1 } });
    return response.data;
  },

  // Get single lead by ID
  getById: async (id) => {
    const response = await axios.get(`${API_URL}/leads/${id}`);
    return response.data;
  },

  // Generate the AI company-analysis report for a lead. Returns { url, markdown, cached? }.
  // Grounded Gemini research — can take 1-3 minutes. A report from the last 30 days
  // comes back instantly with cached: true unless { force: true } is passed.
  generateAnalysis: async (id, { force = false } = {}) => {
    const response = await axios.post(`${API_URL}/leads/${id}/generate-analysis`, force ? { force: true } : {}, { timeout: 295000 });
    return response.data;
  },

  // Create new lead
  create: async (leadData) => {
    try {
      const response = await axios.post(`${API_URL}/leads`, leadData);
      bustCache('follow-up-tracker');
    bustCache('leads');
      return response.data;
    } catch (error) {
      // Re-throw the error with response data if available
      if (error.response && error.response.data) {
        const enhancedError = new Error(error.message || 'Request failed');
        enhancedError.response = { ...error.response, data: error.response.data };
        enhancedError.config = error.config;
        enhancedError.request = error.request;
        throw enhancedError;
      }
      throw error;
    }
  },

  // Update lead
  update: async (id, updates) => {
    const response = await axios.put(`${API_URL}/leads/${id}`, updates);
    bustCache('follow-up-tracker');
    bustCache('leads');
    return response.data;
  },

  // Delete lead
  delete: async (id) => {
    const response = await axios.delete(`${API_URL}/leads/${id}`);
    bustCache('follow-up-tracker');
    bustCache('leads');
    return response.data;
  },

  // Mark follow-up as completed
  // The bin on a Send Emails row. Actually deletes the record, so the row does
  // not come back on the next refresh.
  deleteFollowUp: async (id, followUpNumber, extras = {}) => {
    const response = await axios.post(`${API_URL}/leads/${id}/delete-follow-up`, { followUpNumber, ...extras });
    bustCache('follow-up-tracker');
    bustCache('leads');
    return response.data;
  },

  markCompleted: async (id, followUpNumber = 1, extras = {}) => {
    const response = await axios.patch(`${API_URL}/leads/${id}/mark-completed`, { followUpNumber, ...extras });
    bustCache('follow-up-tracker');
    bustCache('leads');
    return response.data;
  },

  // Bulk import
  bulkImport: async (leads) => {
    const response = await axios.post(`${API_URL}/leads/bulk-import`, { leads });
    return response.data;
  },

  // Upload a meeting-summary file directly onto a lead (no Potential Client requirement).
  // Saves to leads.meeting_summary_file. Returns { success, data: { url, name } }.
  uploadMeetingSummary: async (leadId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const url = `${API_URL}/leads/${leadId}/upload-meeting-summary`;
      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Long timeout — multi-MB PDFs can take a while
        timeout: 120000
      });
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to upload file'
      };
    }
  },

  // Delete ONE meeting-summary file from a lead (multi-file support). Returns
  // the updated file list on success.
  deleteMeetingSummary: async (leadId, url) => {
    try {
      const endpoint = `${API_URL}/leads/${leadId}/meeting-summary`;
      const response = await axios.delete(endpoint, { data: { url } });
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to delete file'
      };
    }
  }
};

// Dashboard API
export const dashboardAPI = {
  // Login authentication
  login: async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/dashboard/login`, { email, password });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  },

  // Forgot password
  // Set a new password (signed in). Returns { success, user, token } — a normal session.
  changePassword: async (currentPassword, newPassword) => {
    const response = await axios.post(`${API_URL}/dashboard/change-password`, { currentPassword, newPassword });
    return response.data;
  },

  forgotPassword: async (email) => {
    try {
      const response = await axios.post(`${API_URL}/dashboard/forgot-password`, { email });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Request failed' };
    }
  },
  
  // Verify reset code
  verifyResetCode: async (email, code) => {
    try {
      const response = await axios.post(`${API_URL}/dashboard/verify-reset-code`, { email, code });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Request failed' };
    }
  },
  
  // Reset password
  resetPassword: async (email, code, newPassword) => {
    try {
      const response = await axios.post(`${API_URL}/dashboard/reset-password`, { email, code, newPassword });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Request failed' };
    }
  },

  // Get dashboard statistics
  getStats: async (params = {}) => {
    const response = await axios.get(`${API_URL}/dashboard/stats`, { params });
    return response.data;
  },

  // Onboarding-funnel analytics (Identified → First Follow-up → Meeting → Onboarded).
  // Cached so switching between Analytics sub-tabs is instant; write paths in this
  // app don't update the onboarding funnel directly, so a 5-min TTL is plenty.
  getOnboardingFunnel: async (params = {}) => {
    const key = 'onboardingFunnel:' + JSON.stringify(params);
    return cachedFetch(key, async () => {
      const response = await axios.get(`${API_URL}/dashboard/analytics/onboarding-funnel`, { params });
      return response.data;
    }, 300000);
  },

  // Update any subset of { identified_date, first_follow_up_date, first_meeting_date,
  // onboarded_date, company_name, bde_name, bde_id } for a single onboarded client.
  // Writes fan out to onboarded_clients + the source lead + follow_ups + post_sales,
  // so busting all related caches keeps the Pre-Sales / Post-Sales / Follow-ups /
  // Onboarded Clients tabs in sync on their next open.
  updateOnboardingFunnelDates: async (onboardedClientId, updates) => {
    const response = await axios.patch(
      `${API_URL}/dashboard/analytics/onboarding-funnel/${onboardedClientId}`,
      updates
    );
    bustCache('onboardingFunnel');
    bustCache('follow-up-tracker');
    bustCache('postSales');
    bustCache('onboardedClients');
    return response.data;
  },

  // Get daily activity
  getDailyActivity: async (params = {}) => {
    const response = await axios.get(`${API_URL}/dashboard/daily-activity`, { params });
    return response.data;
  },

  // Get follow-up tracker. Cached so switching back to the Follow-ups tab is instant
  // (stale data shows immediately while a background refresh runs). Any write path
  // (mark completed, save lead, save post-sales) calls bustCache('follow-up-tracker').
  // Pass { force: true } to force a fresh network fetch (used by explicit refresh flows).
  getFollowUpTracker: async (params = {}) => {
    const { force, ...queryParams } = params;
    const cacheKey = `follow-up-tracker:${JSON.stringify(queryParams)}`;
    if (force) bustCache('follow-up-tracker');
    return cachedFetch(cacheKey, async () => {
      const response = await axios.get(`${API_URL}/dashboard/follow-up-tracker`, { params: queryParams });
      return response.data;
    });
  },

  // Send follow-up emails to POC list (to[], subject, html, companyName?, cc?, bcc?, senderSlot?, userId?, attachments?).
  // Backend enforces user's email_sender_slot when userId is sent, and makes sure a group inbox is
  // CC'd (Sales@amgsol.com on the pitch, AGSSalesFollowUp@amgsol.com on follow-ups).
  // leadId is REQUIRED for threading: the backend keys the conversation on
  // lead + recipient so the initial pitch and follow-ups 1/2/3 stay in one
  // thread. It used to be missing from this signature, so it was stripped here
  // and every email started a brand-new thread.
  // userEmail matters as much as userId: a Suite SSO session carries the Suite
  // subject id, which does not match bde_users.id, so without the email the
  // backend cannot tell who is sending and falls back to the shared Gmail.
  // Five minutes, to match the server's own limit (backend/vercel.json says
  // maxDuration 300). The global default is 30 seconds, and a send to seven
  // recipients with an image-heavy signature takes over a minute — so the
  // browser was giving up while the server calmly went on sending. The mail
  // went out and the screen said "timeout", which is how a row ends up being
  // sent twice.
  sendFollowUpEmails: async ({ to, cc, bcc, subject, html, companyName, senderSlot, userId, userEmail, attachments, leadId, followUpNumber }) => {
    const response = await axios.post(`${API_URL}/dashboard/send-follow-up-emails`,
      { to, cc, bcc, subject, html, companyName, senderSlot, userId, userEmail, attachments, leadId, followUpNumber },
      { timeout: 300000 });
    return response.data;
  },

  // Park an image that was pasted into an email body and get a URL back. The
  // editor uses it when a signature's pictures would otherwise push a template
  // past the 4.5 MB request limit; the send path pulls them back in as inline
  // attachments, so the recipient still sees the images in the message.
  uploadEmailImage: async (dataUrl) => {
    const response = await axios.post(`${API_URL}/dashboard/email-image`, { dataUrl }, { timeout: 120000 });
    return response.data;
  },

  // Which of these leads already have a conversation going with which
  // recipients. The composer uses it to show "continues the thread — goes out as
  // Re: …" BEFORE anything is sent, so the subject a threaded send uses is never
  // a surprise. Keyed "<leadId>|<lower-case email>".
  getEmailThreads: async (leadIds) => {
    const ids = [...new Set((leadIds || []).filter(Boolean).map(String))];
    if (ids.length === 0) return { threads: {} };
    const response = await axios.post(`${API_URL}/dashboard/email-threads`, { leadIds: ids });
    return response.data || { threads: {} };
  },

  // Build the same email as an Outlook draft and get a link that opens it in
  // Outlook on the web, for when you want Outlook's own composer.
  // Same reasoning as the send above: a draft carries the same megabytes.
  createOutlookDraft: async ({ to, cc, bcc, subject, html, attachments, userId, userEmail }) => {
    const response = await axios.post(`${API_URL}/dashboard/create-outlook-draft`,
      { to, cc, bcc, subject, html, attachments, userId, userEmail }, { timeout: 300000 });
    return response.data;
  },

  // List configured email sender accounts (for "Send from" dropdown).
  // Pass the signed-in user to also get `effective` — the address they will
  // ACTUALLY send from (their own Microsoft 365 mailbox, in most cases) — and
  // `graph` { enabled, domains } describing which domains send as themselves.
  getEmailSenders: async ({ userId, email } = {}) => {
    const response = await axios.get(`${API_URL}/dashboard/email-senders`, {
      params: { userId, email }
    });
    return response.data;
  },

  // Email templates per stage (GET all; PUT restricted to admin / Surabhi)
  // scope 'default' reads/writes the shared organisation templates that new
  // accounts inherit; without it you get your own copies.
  // Email history log — what actually went out, newest first.
  getEmailLog: async (params = {}) => {
    const response = await axios.get(`${API_URL}/dashboard/email-log`, { params });
    return response.data;
  },
  getEmailLogFilters: async () => {
    const response = await axios.get(`${API_URL}/dashboard/email-log/filters`);
    return response.data;
  },
  getEmailTemplates: async ({ meta = false, scope } = {}) => {
    const response = await axios.get(`${API_URL}/dashboard/email-templates`, {
      params: { ...(meta ? { meta: 1 } : {}), ...(scope ? { scope } : {}) }
    });
    return response.data;
  },
  // One stage's body, fetched when that stage is opened.
  getEmailTemplateBody: async (stage, scope) => {
    const response = await axios.get(`${API_URL}/dashboard/email-templates/${encodeURIComponent(stage)}`, {
      params: scope ? { scope } : {}
    });
    return response.data;
  },
  updateEmailTemplates: async (userId, templates, scope) => {
    const response = await axios.put(`${API_URL}/dashboard/email-templates`, {
      userId, templates, ...(scope ? { scope } : {})
    });
    return response.data;
  },

  // scope 'all' also returns the team's templates (each tagged `mine` and with
  // an `owner_name`), so a BDA can send with a colleague's template.
  getCustomEmailTemplates: async (userId, scope = 'mine', { meta = false } = {}) => {
    const response = await axios.get(`${API_URL}/dashboard/custom-email-templates`, {
      params: { userId, scope, ...(meta ? { meta: 1 } : {}) }
    });
    return response.data;
  },
  // One custom template with its body, fetched when it is actually chosen.
  getCustomEmailTemplate: async (id) => {
    const response = await axios.get(`${API_URL}/dashboard/custom-email-templates/${id}`);
    return response.data;
  },
  createCustomEmailTemplate: async (userId, payload) => {
    const response = await axios.post(`${API_URL}/dashboard/custom-email-templates`, { userId, ...payload });
    return response.data;
  },
  updateCustomEmailTemplate: async (id, userId, payload) => {
    const response = await axios.put(`${API_URL}/dashboard/custom-email-templates/${id}`, { userId, ...payload });
    return response.data;
  },
  deleteCustomEmailTemplate: async (id, userId) => {
    const response = await axios.delete(`${API_URL}/dashboard/custom-email-templates/${id}`, { params: { userId } });
    return response.data;
  },

  // Get lead count by date
  getLeadCountByDate: async (days = 14) => {
    const response = await axios.get(`${API_URL}/dashboard/lead-count-by-date`, { 
      params: { days } 
    });
    return response.data;
  },

  // Get BDE users (cached — 7 components call this independently on mount).
  // Sorted alphabetically client-side so every BDE dropdown in the app is A→Z,
  // regardless of the DB insertion order or any ordering differences between envs.
  _bdeUsersCache: null,
  _bdeUsersPending: null,
  getBDEUsers: async () => {
    const sortAlpha = (payload) => {
      if (payload && Array.isArray(payload.data)) {
        // Trim before compare — some DB rows have leading/trailing whitespace
        // (e.g. " Siva Yarramsetty") that otherwise sort before 'A'.
        payload.data = [...payload.data].sort((a, b) =>
          (a?.name || '').trim().localeCompare((b?.name || '').trim(), undefined, { sensitivity: 'base' })
        );
      }
      return payload;
    };
    if (dashboardAPI._bdeUsersCache) return dashboardAPI._bdeUsersCache;
    if (dashboardAPI._bdeUsersPending) return dashboardAPI._bdeUsersPending;
    dashboardAPI._bdeUsersPending = axios.get(`${API_URL}/dashboard/bde-users`)
      .then(r => { const sorted = sortAlpha(r.data); dashboardAPI._bdeUsersCache = sorted; dashboardAPI._bdeUsersPending = null; return sorted; })
      .catch(e => { dashboardAPI._bdeUsersPending = null; throw e; });
    return dashboardAPI._bdeUsersPending;
  },

  // Get all users (for user management)
  getAllUsers: async () => {
    const response = await axios.get(`${API_URL}/dashboard/users`);
    return response.data;
  },

  // Create new user
  createUser: async (userData) => {
    const response = await axios.post(`${API_URL}/dashboard/users`, userData);
    return response.data;
  },

  // Update user
  updateUser: async (userId, userData) => {
    const response = await axios.put(`${API_URL}/dashboard/users/${userId}`, userData);
    return response.data;
  },

  // Delete user
  deleteUser: async (userId) => {
    const response = await axios.delete(`${API_URL}/dashboard/users/${userId}`);
    return response.data;
  },

  // Get highlights
  getHighlights: async (params = {}) => {
    const response = await axios.get(`${API_URL}/dashboard/highlights`, { params });
    return response.data;
  },

  // Create highlight
  createHighlight: async (highlightData) => {
    const response = await axios.post(`${API_URL}/dashboard/highlights`, highlightData);
    return response.data;
  },

  // Update highlight
  updateHighlight: async (id, highlightData) => {
    const response = await axios.put(`${API_URL}/dashboard/highlights/${id}`, highlightData);
    return response.data;
  },

  // Delete highlight
  deleteHighlight: async (id) => {
    const response = await axios.delete(`${API_URL}/dashboard/highlights/${id}`);
    return response.data;
  },

  // Get post sales records (5 min cache — busted on writes)
  getPostSales: async (params = {}) => {
    const key = 'postSales:' + JSON.stringify(params);
    return cachedFetch(key, async () => {
      const response = await axios.get(`${API_URL}/dashboard/post-sales`, { params });
      return response.data;
    }, 300000);
  },

  // Get all leads for post sales (5 min cache — busted on writes)
  getPostSalesLeads: async (params = {}) => {
    const key = 'postSalesLeads:' + JSON.stringify(params);
    return cachedFetch(key, async () => {
      const response = await axios.get(`${API_URL}/dashboard/post-sales/leads`, { params });
      return response.data;
    }, 300000);
  },

  // Create or update post sales record
  createOrUpdatePostSales: async (postSalesData) => {
    const response = await axios.post(`${API_URL}/dashboard/post-sales`, postSalesData);
    bustCache('postSales');
    bustCache('follow-up-tracker');
    return response.data;
  },

  // Update post sales stage
  updatePostSalesStage: async (id, stage) => {
    const response = await axios.put(`${API_URL}/dashboard/post-sales/${id}/stage`, { stage });
    bustCache('postSales');
    bustCache('follow-up-tracker');
    return response.data;
  },

  // Convert to potential client
  convertToPotentialClient: async (lead_id, converted_by) => {
    const response = await axios.post(`${API_URL}/dashboard/convert-to-potential-client`, {
      lead_id,
      converted_by
    });
    bustCache('potentialClients');
    return response.data;
  },

  // Convert post sales to potential client
  convertPostSalesToPotentialClient: async (lead_id, converted_by) => {
    try {
      const response = await axios.post(`${API_URL}/dashboard/convert-post-sales-to-potential-client`, {
        lead_id,
        converted_by
      });
      bustCache('potentialClients');
      bustCache('postSales');
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Request failed'
      };
    }
  },

  // Check if a lead is already in potential clients
  checkLeadInPotentialClients: async (lead_id) => {
    try {
      console.log('🔍 API Call - Checking lead in potential clients:', lead_id);
      console.log('🔍 API_URL:', API_URL);
      const url = `${API_URL}/dashboard/potential-clients/check-lead/${lead_id}`;
      console.log('🔍 Full URL:', url);
      const response = await axios.get(url);
      console.log('✅ API Response for checkLeadInPotentialClients:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error checking lead in potential clients:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error URL:', error.config?.url);
      // Return error but don't default to false - let the component handle it
      return { 
        success: false, 
        isInPotentialClients: false,
        error: error.response?.data?.error || error.message,
        data: null
      };
    }
  },

  // Get potential clients (5 min cache — busted on writes)
  createSuiteSession: async ({ suite_token, email }) => {
    const response = await axios.post(`${API_URL}/dashboard/suite-session`, { suite_token, email });
    return response.data;
  },

  // ---- Pipeline clients (Potential -> Pipeline -> Onboarded) ----
  getPipelineClients: async (params = {}) => {
    const response = await axios.get(`${API_URL}/dashboard/pipeline-clients`, { params });
    return response.data;
  },
  convertPotentialToPipeline: async (potential_client_id, extra = {}) => {
    const response = await axios.post(`${API_URL}/dashboard/pipeline-clients/convert-from-potential`, {
      potential_client_id, ...extra
    });
    return response.data;
  },
  convertPipelineToOnboarded: async (pipeline_client_id, onboarding_date, onboarded_by) => {
    const response = await axios.post(`${API_URL}/dashboard/onboarded-clients/convert-from-pipeline`, {
      pipeline_client_id, onboarding_date, onboarded_by
    });
    return response.data;
  },
  updatePipelineClient: async (id, updates) => {
    const response = await axios.put(`${API_URL}/dashboard/pipeline-clients/${id}`, updates);
    return response.data;
  },
  movePipelineBackToPotential: async (id) => {
    const response = await axios.post(`${API_URL}/dashboard/pipeline-clients/${id}/back-to-potential`);
    return response.data;
  },
  deletePipelineClient: async (id) => {
    const response = await axios.delete(`${API_URL}/dashboard/pipeline-clients/${id}`);
    return response.data;
  },

  // ---- Scheduled meetings ----
  getMeetings: async (params = {}) => {
    const response = await axios.get(`${API_URL}/dashboard/meetings`, { params });
    return response.data;
  },
  createMeeting: async (payload) => {
    const response = await axios.post(`${API_URL}/dashboard/meetings`, payload);
    return response.data;
  },
  updateMeeting: async (id, payload) => {
    const response = await axios.put(`${API_URL}/dashboard/meetings/${id}`, payload);
    return response.data;
  },
  // Calls the meeting off and tells the attendees; the record stays, marked Cancelled.
  cancelMeeting: async (id, comment) => {
    const response = await axios.post(`${API_URL}/dashboard/meetings/${id}/cancel`, { comment });
    return response.data;
  },
  deleteMeeting: async (id) => {
    const response = await axios.delete(`${API_URL}/dashboard/meetings/${id}`);
    return response.data;
  },

  getPotentialClients: async (params = {}) => {
    const key = 'potentialClients:' + JSON.stringify(params);
    return cachedFetch(key, async () => {
      const response = await axios.get(`${API_URL}/dashboard/potential-clients`, { params });
      return response.data;
    }, 300000);
  },

  // Delete potential client
  deletePotentialClient: async (id) => {
    const apiUrl = getApiUrl();
    try {
      const response = await axios.delete(`${apiUrl}/dashboard/potential-clients/${id}`);
      bustCache('potentialClients');
      return response.data;
    } catch (error) {
      console.error('Error deleting potential client:', error.response?.data || error.message);
      throw error;
    }
  },

  // Restore client from leads to potential clients
  restoreClientFromLeads: async (lead_id) => {
    const response = await axios.post(`${API_URL}/dashboard/potential-clients/restore-from-leads`, {
      lead_id
    });
    return response.data;
  },

  // Upload meeting summary file for potential client
  uploadMeetingSummary: async (potentialClientId, file) => {
    try {
      console.log('📤 Uploading meeting summary for potential client:', potentialClientId);
      console.log('📤 File:', file.name, file.type, file.size);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('potential_client_id', potentialClientId);
      
      console.log('📤 API_URL:', API_URL);
      const url = `${API_URL}/dashboard/potential-clients/upload-meeting-summary`;
      console.log('📤 Full URL:', url);
      
      const response = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('✅ Upload response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error uploading meeting summary:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to upload file'
      };
    }
  },

  // Get meeting summary file URL
  getMeetingSummaryUrl: (potentialClientId) => {
    return `${API_URL}/dashboard/potential-clients/${potentialClientId}/meeting-summary`;
  },

  // Mark post-sales follow-up as completed
  markPostSalesFollowUpCompleted: async (lead_id, followUpNumber = 1, extras = {}) => {
    try {
      const response = await axios.patch(
        `${API_URL}/dashboard/post-sales/${lead_id}/mark-follow-up-completed`,
        { followUpNumber, ...extras }
      );
      bustCache('postSales');
      bustCache('follow-up-tracker');
      return response.data;
    } catch (error) {
      console.error('Error in markPostSalesFollowUpCompleted API call:', error);
      throw error;
    }
  },

  // Trigger n8n webhook to send emails
  triggerEmailWebhook: async () => {
    try {
      const response = await axios.post(`${API_URL}/dashboard/trigger-email-webhook`, {
        timestamp: new Date().toISOString(),
        source: 'follow-up-tracker'
      });
      return response.data;
    } catch (error) {
      console.error('Error triggering email webhook:', error);
      throw error;
    }
  },

  // Onboarded Clients API (5 min cache — busted on writes)
  getOnboardedClients: async (params = {}) => {
    const key = 'onboardedClients:' + JSON.stringify(params);
    return cachedFetch(key, async () => {
      const response = await axios.get(`${API_URL}/dashboard/onboarded-clients`, { params });
      return response.data;
    }, 300000);
  },

  closeDealFromPreSales: async (lead_id, onboarding_date, onboarded_by) => {
    try {
      const url = `${API_URL}/dashboard/onboarded-clients/close-deal`;
      console.log('🔗 Calling closeDealFromPreSales:', url);
      console.log('📦 Request data:', { lead_id, onboarding_date, onboarded_by });
      const response = await axios.post(url, {
        lead_id,
        onboarding_date,
        onboarded_by
      });
      console.log('✅ Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error in closeDealFromPreSales:', error);
      console.error('❌ Error URL:', error.config?.url);
      console.error('❌ Error response:', error.response?.data);
      throw error;
    }
  },

  convertPotentialClientToOnboarded: async (potential_client_id, onboarding_date, onboarded_by) => {
    const apiUrl = getApiUrl();
    try {
      const response = await axios.post(`${apiUrl}/dashboard/onboarded-clients/convert-from-potential`, {
        potential_client_id,
        onboarding_date,
        onboarded_by
      });
      bustCache('potentialClients');
      bustCache('onboardedClients');
      return response.data;
    } catch (error) {
      console.error('Error converting potential client to onboarded:', error.response?.data || error.message);
      throw error;
    }
  },

  updateOnboardedClient: async (id, updates) => {
    try {
      const response = await axios.put(`${API_URL}/dashboard/onboarded-clients/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('❌ updateOnboardedClient failed:', error.response?.status, error.response?.data);
      throw error;
    }
  },

  deleteOnboardedClient: async (id) => {
    const apiUrl = getApiUrl();
    try {
      const response = await axios.delete(`${apiUrl}/dashboard/onboarded-clients/${id}`);
      bustCache('onboardedClients');
      bustCache('potentialClients');
      return response.data;
    } catch (error) {
      console.error('Error deleting onboarded client:', error.response?.data || error.message);
      throw error;
    }
  },

  uploadOnboardedClientDocument: async (onboardedClientId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('onboarded_client_id', onboardedClientId);
      
      const response = await axios.post(`${API_URL}/dashboard/onboarded-clients/upload-document`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to upload file'
      };
    }
  },
};

// AI Assistant API — longer timeout because Gemini may need 20-40 s
export const aiAPI = {
  chat: async (question, conversationHistory = []) => {
    const response = await axios.post(
      `${API_URL}/ai/chat`,
      { question, conversationHistory },
      { timeout: 120000 }          // 2 min for AI calls
    );
    return response.data;
  },
  getSuggestions: async () => {
    const response = await axios.get(`${API_URL}/ai/suggestions`);
    return response.data;
  }
};

// Lead Gen Agent API — Apollo-backed search, enrichment, scheduling
export const leadGenAPI = {
  health: async () => {
    const r = await axios.get(`${API_URL}/lead-gen/health`);
    return r.data;
  },
  search: async ({ filters, maxResults = 10, autoImport = false, roundRobin = true, requestedBy = null } = {}) => {
    const r = await axios.post(`${API_URL}/lead-gen/search`, {
      filters, max_results: maxResults, auto_import: autoImport, round_robin: roundRobin, requested_by: requestedBy
    }, { timeout: 60000 });
    return r.data;
  },
  research: async ({ filters, maxResults = 5, autoImport = false, roundRobin = true, requestedBy = null } = {}) => {
    const r = await axios.post(`${API_URL}/lead-gen/research`, {
      filters, max_results: maxResults, auto_import: autoImport, round_robin: roundRobin, requested_by: requestedBy
    }, { timeout: 180000 });
    return r.data;
  },
  importCandidate: async (id, { assignToBdeId = null, reviewedBy = null } = {}) => {
    const r = await axios.post(`${API_URL}/lead-gen/candidates/${id}/import`, { assign_to_bde_id: assignToBdeId, reviewed_by: reviewedBy });
    return r.data;
  },
  dismissCandidate: async (id, reviewedBy = null) => {
    const r = await axios.post(`${API_URL}/lead-gen/candidates/${id}/dismiss`, { reviewed_by: reviewedBy });
    return r.data;
  },
  candidatesForRun: async (runId) => {
    const r = await axios.get(`${API_URL}/lead-gen/runs/${runId}/candidates`);
    return r.data;
  },
  enrichLead: async (leadId, { dryRun = false, missingOnly = true } = {}) => {
    const r = await axios.post(`${API_URL}/lead-gen/enrich/${leadId}`, { dry_run: dryRun, missing_only: missingOnly }, { timeout: 60000 });
    return r.data;
  },
  enrichBulk: async ({ filters, dryRun = false, missingOnly = true, maxResults = 25, requestedBy = null } = {}) => {
    const r = await axios.post(`${API_URL}/lead-gen/enrich-bulk`, {
      filters, dry_run: dryRun, missing_only: missingOnly, max_results: maxResults, requested_by: requestedBy
    }, { timeout: 120000 });
    return r.data;
  },
  listSchedules: async () => {
    const r = await axios.get(`${API_URL}/lead-gen/schedules`);
    return r.data;
  },
  createSchedule: async (payload) => {
    const r = await axios.post(`${API_URL}/lead-gen/schedules`, payload);
    return r.data;
  },
  updateSchedule: async (id, patch) => {
    const r = await axios.patch(`${API_URL}/lead-gen/schedules/${id}`, patch);
    return r.data;
  },
  deleteSchedule: async (id) => {
    const r = await axios.delete(`${API_URL}/lead-gen/schedules/${id}`);
    return r.data;
  },
  runScheduleNow: async (id, requestedBy = null) => {
    const r = await axios.post(`${API_URL}/lead-gen/schedules/${id}/run-now`, { requested_by: requestedBy }, { timeout: 120000 });
    return r.data;
  },
  listRuns: async (limit = 20) => {
    const r = await axios.get(`${API_URL}/lead-gen/runs`, { params: { limit } });
    return r.data;
  }
};

// ─── Staging Leads ("Leads" tab) ────────────────────────────────────────────
export const stagingLeadsAPI = {
  list: async () => {
    const r = await axios.get(`${API_URL}/staging-leads`);
    return r.data;
  },
  add: async (payload) => {
    const r = await axios.post(`${API_URL}/staging-leads`, payload);
    return r.data;
  },
  remove: async (id) => {
    const r = await axios.delete(`${API_URL}/staging-leads/${id}`);
    return r.data;
  },
  import: async (id, { assignToBdeId = null } = {}) => {
    const r = await axios.post(`${API_URL}/staging-leads/${id}/import`, { assign_to_bde_id: assignToBdeId });
    return r.data;
  },
  findCompetitors: async ({ companyName, companyUrl = '', location = '', count = 6, grounded = true } = {}) => {
    const r = await axios.post(`${API_URL}/staging-leads/find-competitors`, {
      company_name: companyName, company_url: companyUrl, location, count, grounded
    }, { timeout: 180000 });
    return r.data;
  },
  parsePaste: async ({ text, relatedTo = '' } = {}) => {
    const r = await axios.post(`${API_URL}/staging-leads/parse-paste`, {
      text, related_to: relatedTo
    }, { timeout: 120000 });
    return r.data;
  }
};

const api = { leadsAPI, dashboardAPI, aiAPI, leadGenAPI, stagingLeadsAPI };
export default api;
